import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';
import { getApiBaseUrl } from '../../utils/apiUrls';

const API_BASE_URL = getApiBaseUrl();

// 비동기 액션들
export const createTransfer = createAsyncThunk(
  'transfer/create',
  async (transferData, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(`${API_BASE_URL}/transfer/create`, transferData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const executeTransfer = createAsyncThunk(
  'transfer/execute',
  async ({ linkId, walletAddress, proof, publicSignals }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(`${API_BASE_URL}/transfer/execute/${linkId}`, {
        walletAddress,
        proof,
        publicSignals
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '송금 실행 실패'));
    }
  }
);

export const getTransferHistory = createAsyncThunk(
  'transfer/history',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.get(`${API_BASE_URL}/transfer/history`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '송금 내역 조회 실패'));
    }
  }
);

export const getTransferById = createAsyncThunk(
  'transfer/getById',
  async (transferId, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.get(`${API_BASE_URL}/transfer/${transferId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '송금 정보 조회 실패'));
    }
  }
);

/** 공개 claim 토큰으로 메타 조회 (로그인 불필요) */
export const getPublicClaimInfo = createAsyncThunk(
  'transfer/publicClaim',
  async (claimToken, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/transfer/public/${claimToken}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'claim 정보 조회 실패'));
    }
  }
);

/** EscrowClaim.claim 용 operator 서명 */
export const requestEscrowClaimSignature = createAsyncThunk(
  'transfer/escrowClaimSignature',
  async ({ transferId, recipientAddress, deadline }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        `${API_BASE_URL}/transfer/${transferId}/escrow/claim-signature`,
        { recipientAddress, deadline },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'claim 서명 실패'));
    }
  }
);

export const confirmEscrowClaimTx = createAsyncThunk(
  'transfer/confirmEscrowClaim',
  async ({ transferId, txHash }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        `${API_BASE_URL}/transfer/${transferId}/escrow/confirm-claim`,
        { txHash },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'claim 확인 실패'));
    }
  }
);

export const confirmEscrowDepositTx = createAsyncThunk(
  'transfer/confirmEscrowDeposit',
  async ({ transferId, txHash }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        `${API_BASE_URL}/transfer/${transferId}/escrow/confirm-deposit`,
        { txHash },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '입금 확인 실패'));
    }
  }
);

export const confirmEscrowRefundTx = createAsyncThunk(
  'transfer/confirmEscrowRefund',
  async ({ transferId, txHash }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        `${API_BASE_URL}/transfer/${transferId}/escrow/confirm-refund`,
        { txHash },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '환불 확인 실패'));
    }
  }
);

/** 발신자용 온체인/비즈니스 이벤트 타임라인 */
export const getTransferEvents = createAsyncThunk(
  'transfer/events',
  async (transferId, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.get(`${API_BASE_URL}/transfer/${transferId}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '이벤트 조회 실패'));
    }
  }
);

// 초기 상태
const initialState = {
  transfers: [],
  currentTransfer: null,
  transferLink: null,
  publicClaim: null,
  escrowClaimSignature: null,
  transferEvents: [],
  loading: false,
  error: null,
  success: null
};

// 슬라이스 생성
const transferSlice = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    // 상태 초기화
    clearTransferState: (state) => {
      state.currentTransfer = null;
      state.transferLink = null;
      state.publicClaim = null;
      state.escrowClaimSignature = null;
      state.error = null;
      state.success = null;
    },
    
    // 에러 초기화
    clearError: (state) => {
      state.error = null;
    },
    
    // 성공 메시지 초기화
    clearSuccess: (state) => {
      state.success = null;
    },
    
    // 송금 링크 설정
    setTransferLink: (state, action) => {
      state.transferLink = action.payload;
    },
    
    // 현재 송금 설정
    setCurrentTransfer: (state, action) => {
      state.currentTransfer = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 송금 생성
      .addCase(createTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransfer = action.payload;
        state.transferLink = action.payload.transferLink;
        state.success = '송금 링크가 생성되었습니다';
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(action.payload, '송금 생성 실패');
      })
      
      // 송금 실행
      .addCase(executeTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(executeTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransfer = action.payload;
        state.success = '송금이 완료되었습니다';
      })
      .addCase(executeTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 송금 내역 조회
      .addCase(getTransferHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransferHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = action.payload.transfers || [];
      })
      .addCase(getTransferHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 송금 정보 조회
      .addCase(getTransferById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransferById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransfer = action.payload.transfer || action.payload;
      })
      .addCase(getTransferById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getPublicClaimInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.publicClaim = null;
      })
      .addCase(getPublicClaimInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.publicClaim = action.payload.data || null;
      })
      .addCase(getPublicClaimInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(requestEscrowClaimSignature.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.escrowClaimSignature = null;
      })
      .addCase(requestEscrowClaimSignature.fulfilled, (state, action) => {
        state.loading = false;
        state.escrowClaimSignature = action.payload.data || null;
      })
      .addCase(requestEscrowClaimSignature.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(action.payload, 'claim 서명 실패');
      })
      .addCase(getTransferEvents.fulfilled, (state, action) => {
        state.transferEvents = action.payload?.data?.events || [];
      })
      .addCase(getTransferEvents.rejected, (state) => {
        state.transferEvents = [];
      });
  }
});

// 액션 내보내기
export const { 
  clearTransferState, 
  clearError, 
  clearSuccess, 
  setTransferLink, 
  setCurrentTransfer 
} = transferSlice.actions;

// 선택자들
export const selectTransfer = (state) => state.transfer;
export const selectTransfers = (state) => state.transfer.transfers;
export const selectCurrentTransfer = (state) => state.transfer.currentTransfer;
export const selectTransferLink = (state) => state.transfer.transferLink;
export const selectPublicClaim = (state) => state.transfer.publicClaim;
export const selectEscrowClaimSignature = (state) => state.transfer.escrowClaimSignature;
export const selectTransferEvents = (state) => state.transfer.transferEvents;
export const selectTransferLoading = (state) => state.transfer.loading;
export const selectTransferError = (state) => state.transfer.error;
export const selectTransferSuccess = (state) => state.transfer.success;

// 리듀서 내보내기
export default transferSlice.reducer; 